import { getCanonicalTeamName } from '../config/teamAliases';
import { normalizeSearchText } from '../utils/searchText';

export const NEWS_CACHE_TTL = 5 * 60 * 1000;

const newsCache = new Map();
const activeNewsRequests = new Map();

let cacheGeneration = 0;

const logNewsCache = (event, key) => {
  if (process.env.NODE_ENV === 'development') {
    console.debug(`[${event}] ${key}`);
  }
};

export const normalizeNewsCacheKeyPart = (value) => {
  return String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ');
};

const NEWS_QUERY_KEY_FIELDS = [
  'queryType',
  'category',
  'section',
  'subsection',
  'club',
  'team',
  'tag',
  'tournament',
  'authorId',
  'authorName',
  'articleId',
  'order',
  'page',
  'limit',
];

export const buildNewsQueryCacheKey = (query = {}) => {
  const parts = NEWS_QUERY_KEY_FIELDS.reduce((keyParts, field) => {
    const value = normalizeNewsCacheKeyPart(query[field]);

    if (value) {
      keyParts.push(`${field}=${value}`);
    }

    return keyParts;
  }, []);

  return `news-list:${parts.join(':')}`;
};

export const newsCacheKeys = {
  home: (limitCount) =>
    buildNewsQueryCacheKey({
      queryType: 'home',
      order: 'publishedAt:desc',
      limit: limitCount,
    }),
  category: (category, limitCount) =>
    buildNewsQueryCacheKey({
      queryType: 'category',
      category,
      order: 'publishedAt:desc',
      limit: limitCount,
    }),
  section: (section, limitCount) =>
    buildNewsQueryCacheKey({
      queryType: 'section',
      section: getCanonicalTeamName(section),
      order: 'publishedAt:desc',
      limit: limitCount,
    }),
  categorySection: (category, section, limitCount) =>
    buildNewsQueryCacheKey({
      queryType: 'category-section',
      category,
      section: getCanonicalTeamName(section),
      order: 'publishedAt:desc',
      limit: limitCount,
    }),
  popular: (limitCount) =>
    buildNewsQueryCacheKey({
      queryType: 'popular',
      order: 'views:desc,publishedAt:desc',
      limit: limitCount,
    }),
  all: (limitCount) =>
    buildNewsQueryCacheKey({
      queryType: 'all',
      order: 'publishedAt:desc',
      limit: limitCount,
    }),
  search: (tag, limitCount) =>
    buildNewsQueryCacheKey({
      queryType: 'search',
      tag: normalizeSearchText(tag),
      order: 'publishedAt:desc',
      limit: limitCount,
    }),
  author: (authorId, authorName, limitCount = 'all') =>
    buildNewsQueryCacheKey({
      queryType: 'author',
      authorId,
      authorName,
      order: 'publishedAt:desc',
      limit: limitCount,
    }),
  adminPage: ({
    page,
    limit: limitCount,
    category,
    authorId,
    order = 'publishedAt:desc',
  }) =>
    buildNewsQueryCacheKey({
      queryType: 'admin',
      category,
      authorId,
      order,
      page,
      limit: limitCount,
    }),
  related: (currentNews, limitCount) => {
    const firstSection = Array.isArray(currentNews?.sections)
      ? currentNews.sections[0]
      : null;

    return buildNewsQueryCacheKey({
      queryType: 'related',
      category: firstSection ? undefined : currentNews?.category,
      section: getCanonicalTeamName(firstSection),
      articleId: currentNews?.id,
      order: 'publishedAt:desc',
      limit: limitCount,
    });
  },
};

export const getCachedNews = (key) => {
  return newsCache.get(key)?.data;
};

export const getCachedNewsBySection = (section, limitCount = 25) => {
  const normalizedSection = normalizeNewsCacheKeyPart(
    getCanonicalTeamName(section)
  );

  if (!normalizedSection) {
    return [];
  }

  const matchingNews = [];
  const seenIds = new Set();

  newsCache.forEach(({ data }) => {
    if (!Array.isArray(data)) {
      return;
    }

    data.forEach((news) => {
      const matchesSection =
        Array.isArray(news?.sections)
        && news.sections.some(
          (value) => normalizeNewsCacheKeyPart(
            getCanonicalTeamName(value)
          ) === normalizedSection
        );

      if (matchesSection && news.id && !seenIds.has(news.id)) {
        seenIds.add(news.id);
        matchingNews.push(news);
      }
    });
  });

  return matchingNews.slice(0, limitCount);
};

const getFreshCachedNews = (key) => {
  const entry = newsCache.get(key);

  if (!entry || Date.now() - entry.timestamp >= NEWS_CACHE_TTL) {
    return undefined;
  }

  return entry.data;
};

export const loadNewsWithCache = (key, request) => {
  const cacheEntry = newsCache.get(key);
  const cachedData = getFreshCachedNews(key);

  if (cachedData !== undefined) {
    logNewsCache('CACHE HIT', key);
    return Promise.resolve(cachedData);
  }

  logNewsCache(cacheEntry ? 'CACHE STALE' : 'CACHE MISS', key);

  const activeRequest = activeNewsRequests.get(key);

  if (activeRequest) {
    logNewsCache('REQUEST DEDUPLICATED', key);
    return activeRequest;
  }

  const requestGeneration = cacheGeneration;
  logNewsCache('REQUEST START', key);
  const requestPromise = Promise.resolve()
    .then(request)
    .then((data) => {
      if (requestGeneration === cacheGeneration) {
        newsCache.set(key, {
          data,
          timestamp: Date.now(),
        });
      }

      return data;
    })
    .finally(() => {
      if (activeNewsRequests.get(key) === requestPromise) {
        activeNewsRequests.delete(key);
      }
    });

  activeNewsRequests.set(key, requestPromise);

  return requestPromise;
};

export const invalidateNewsCache = () => {
  cacheGeneration += 1;
  newsCache.clear();
  activeNewsRequests.clear();
};
